import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Route, Routes, useNavigate } from "react-router-dom";
import { supabase } from "./supabase";
import { OverridesPage } from "./pages/Overrides";
import { AnnouncementsPage } from "./pages/Announcements";
import { RoutesPage } from "./pages/Routes";
import { DriversPage } from "./pages/Drivers";
import { TabsBar } from "./components/TabsBar";

// ---------------------------------------------------------------------------
// Types — mirror public.* tables from the initial schema
// ---------------------------------------------------------------------------

type RouteRow = {
  id: number;
  name: string;
};

type Vehicle = {
  id: number;
  label: string;
  active: boolean;
};

type ActiveTrip = {
  id: number;
  vehicle_id: number;
  route_id: number;
  status: "active" | "completed" | "silent" | "cancelled";
  started_at: string;
};

type LivePosition = {
  vehicle_id: number;
  recorded_at: string;
  received_at: string;
};

type BusStatus = "LIVE" | "SILENT" | "IDLE";

type BusRow = {
  vehicleId: number;
  label: string;
  routeName: string | null;
  /** Age in ms of the latest ping, or null when there's no live position. */
  pingAgeMs: number | null;
  status: BusStatus;
};

const REFRESH_MS = 30_000;
const SILENT_THRESHOLD_MS = 90_000;

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

async function fetchFleet(): Promise<BusRow[]> {
  // Four small parallel reads — clearer FK handling than nested selects,
  // and each table is tiny (≤20 buses, ≤20 routes, ≤20 live positions).
  const [vehiclesRes, tripsRes, liveRes, routesRes] = await Promise.all([
    supabase.from("vehicles").select("id, label, active").eq("active", true),
    supabase
      .from("trips")
      .select("id, vehicle_id, route_id, status, started_at")
      .eq("status", "active"),
    supabase
      .from("live_positions")
      .select("vehicle_id, recorded_at, received_at"),
    supabase.from("routes").select("id, name"),
  ]);

  if (vehiclesRes.error) throw vehiclesRes.error;
  if (tripsRes.error) throw tripsRes.error;
  if (liveRes.error) throw liveRes.error;
  if (routesRes.error) throw routesRes.error;

  const vehicles = (vehiclesRes.data ?? []) as Vehicle[];
  const trips = (tripsRes.data ?? []) as ActiveTrip[];
  const lives = (liveRes.data ?? []) as LivePosition[];
  const routes = (routesRes.data ?? []) as RouteRow[];

  const routeById = new Map<number, string>(
    routes.map((r) => [r.id, r.name]),
  );
  const activeTripByVehicle = new Map<number, ActiveTrip>(
    trips.map((t) => [t.vehicle_id, t]),
  );
  const liveByVehicle = new Map<number, LivePosition>(
    lives.map((l) => [l.vehicle_id, l]),
  );

  const now = Date.now();

  return vehicles.map<BusRow>((v) => {
    const trip = activeTripByVehicle.get(v.id);
    const live = liveByVehicle.get(v.id);

    let status: BusStatus;
    let pingAgeMs: number | null = null;

    if (!trip) {
      // No active shift → bus is idle, regardless of stale data.
      status = "IDLE";
    } else if (!live) {
      // Active shift but no live position row yet → treat as silent.
      status = "SILENT";
    } else {
      pingAgeMs = Math.max(
        0,
        now - new Date(live.received_at).getTime(),
      );
      status = pingAgeMs <= SILENT_THRESHOLD_MS ? "LIVE" : "SILENT";
    }

    return {
      vehicleId: v.id,
      label: v.label,
      routeName: trip ? routeById.get(trip.route_id) ?? null : null,
      pingAgeMs,
      status,
    };
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAge(ms: number | null): string {
  if (ms === null) return "—";
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remSec = seconds % 60;
  return `${minutes}m ${remSec}s`;
}

const statusColor: Record<BusStatus, string> = {
  LIVE: "#1f9d55", // green
  SILENT: "#c0392b", // red
  IDLE: "#9aa0a6", // grey
};

// ---------------------------------------------------------------------------
// Pages
// ---------------------------------------------------------------------------

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const { error: signInError } =
      await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (signInError) {
      setError(signInError.message);
      return;
    }
    navigate("/dashboard", { replace: true });
  };

  return (
    <main style={styles.center}>
      <form onSubmit={onSubmit} style={styles.card}>
        <h1 style={styles.h1}>OMJ Admin</h1>
        <p style={styles.muted}>Sign in to the fleet dashboard.</p>

        <label style={styles.label}>
          Email
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
          />
        </label>

        <label style={styles.label}>
          Password
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
          />
        </label>

        {error && <p style={styles.error}>{error}</p>}

        <button type="submit" disabled={submitting} style={styles.button}>
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </main>
  );
}

function DashboardPage() {
  const navigate = useNavigate();
  const [buses, setBuses] = useState<BusRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    try {
      const rows = await fetchFleet();
      setBuses(rows);
      setError(null);
      setLastUpdated(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => {
      void refresh();
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const silentBuses = useMemo(
    () => buses.filter((b) => b.status === "SILENT"),
    [buses],
  );

  const onSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/login", { replace: true });
  };

  return (
    <main style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.h1}>Fleet dashboard</h1>
        <div style={styles.headerRight}>
          <span style={styles.muted}>
            {lastUpdated
              ? `Updated ${new Date(lastUpdated).toLocaleTimeString()}`
              : "Loading…"}
          </span>
          <button onClick={() => void refresh()} style={styles.smallButton}>
            Refresh
          </button>
          <button onClick={onSignOut} style={styles.smallButton}>
            Sign out
          </button>
        </div>
      </header>

      <TabsBar active="dashboard" />

      <nav style={styles.nav}>
        <Link to="/overrides" style={styles.navLink}>
          Schedule overrides
        </Link>
        <Link to="/announcements" style={styles.navLink}>
          Trip cancellations
        </Link>
      </nav>

      {silentBuses.length > 0 && (
        <div role="alert" style={styles.alert}>
          ⚠ SILENT ALERT — {silentBuses.length} bus
          {silentBuses.length === 1 ? "" : "es"} silent &gt; 90s:{" "}
          {silentBuses.map((b) => b.label).join(", ")}
        </div>
      )}

      {error && (
        <div style={{ ...styles.alert, background: "#fdecea", color: "#a02622" }}>
          Failed to load fleet: {error}
        </div>
      )}

      {loading && buses.length === 0 ? (
        <p style={styles.muted}>Loading fleet…</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Bus label</th>
              <th style={styles.th}>Route</th>
              <th style={styles.th}>Last ping age</th>
              <th style={styles.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {buses.map((b) => (
              <tr key={b.vehicleId}>
                <td style={styles.td}>{b.label}</td>
                <td style={styles.td}>{b.routeName ?? "—"}</td>
                <td style={styles.td}>{formatAge(b.pingAgeMs)}</td>
                <td style={styles.td}>
                  <span
                    style={{
                      ...styles.badge,
                      background: statusColor[b.status],
                    }}
                  >
                    {b.status}
                  </span>
                </td>
              </tr>
            ))}
            {buses.length === 0 && !loading && (
              <tr>
                <td style={styles.td} colSpan={4}>
                  No active vehicles in the fleet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Auth gate + routing
// ---------------------------------------------------------------------------

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      if (!data.session) {
        navigate("/login", { replace: true });
      }
      setChecking(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) navigate("/login", { replace: true });
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [navigate]);

  if (checking) return <p style={styles.muted}>Checking session…</p>;
  return <>{children}</>;
}

function RootRedirect() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      navigate(data.session ? "/dashboard" : "/login", { replace: true });
      setReady(true);
    });
  }, [navigate]);

  if (!ready) return <p style={styles.muted}>Loading…</p>;
  return null;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/routes"
        element={
          <ProtectedRoute>
            <RoutesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/drivers"
        element={
          <ProtectedRoute>
            <DriversPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/overrides"
        element={
          <ProtectedRoute>
            <OverridesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/announcements"
        element={
          <ProtectedRoute>
            <AnnouncementsPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

// ---------------------------------------------------------------------------
// Styles (inline — keep zero extra deps for the MVP)
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "system-ui, -apple-system, sans-serif",
    maxWidth: 960,
    margin: "0 auto",
    padding: 24,
    color: "#1a1a1a",
  },
  center: {
    display: "flex",
    minHeight: "100vh",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  card: {
    width: 320,
    padding: 24,
    border: "1px solid #ddd",
    borderRadius: 8,
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  headerRight: { display: "flex", alignItems: "center", gap: 12 },
  nav: {
    display: "flex",
    gap: 8,
    marginBottom: 16,
  },
  navLink: {
    padding: "6px 12px",
    background: "#fff",
    color: "#1a3a5c",
    border: "1px solid #1a3a5c",
    borderRadius: 4,
    textDecoration: "none",
    fontSize: 13,
    fontWeight: 600,
  },
  h1: { margin: 0, fontSize: 20 },
  muted: { color: "#666", fontSize: 14 },
  label: { display: "flex", flexDirection: "column", gap: 4, fontSize: 14 },
  input: {
    padding: "8px 10px",
    border: "1px solid #ccc",
    borderRadius: 4,
    fontSize: 14,
  },
  button: {
    marginTop: 8,
    padding: "10px 12px",
    background: "#1a3a5c",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 14,
  },
  smallButton: {
    padding: "6px 10px",
    background: "#f1f1f1",
    color: "#1a1a1a",
    border: "1px solid #ccc",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 13,
  },
  error: { color: "#c0392b", fontSize: 13, margin: 0 },
  alert: {
    padding: "10px 14px",
    background: "#fff4e5",
    color: "#a05a00",
    border: "1px solid #f0c674",
    borderRadius: 6,
    marginBottom: 16,
    fontWeight: 600,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: "2px solid #1a3a5c",
  },
  td: { padding: "8px 10px", borderBottom: "1px solid #eee" },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    color: "#fff",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.5,
  },
};
